import type { PortalHelpConfig } from './portalHelpTypes';

export const FORMATEUR_HELP: PortalHelpConfig = {
  pageTitle: 'Centre d’aide formateur',
  pageDescription: 'Sessions, e-learning, rémunération, profil et impact académique.',
  roleLabel: 'Formateur',
  intro:
    'Ce guide détaille chaque module de votre portail formateur. Suivez les étapes dans l’ordre lors de votre première connexion, puis revenez à la carte correspondant à votre question du moment — la majorité des tâches (présences, notes, e-learning) se font sans assistance.',
  gettingStarted: {
    title: 'Première connexion',
    steps: [
      'Connectez-vous avec l’identifiant reçu du centre (téléphone ou email selon configuration).',
      'Si vous êtes redirigé automatiquement vers Mon profil, complétez toutes les sections obligatoires (identité, spécialité, pièces) puis enregistrez.',
      'Changez le mot de passe temporaire si la plateforme l’exige : mot de passe fort, confirmation identique.',
      'Ouvrez Mes sessions : vérifiez que vos vacations planifiées apparaissent avec la bonne cohorte et la bonne salle.',
      'Consultez E-learning pour voir les cours dont vous êtes référent et les travaux en attente de correction.',
    ],
  },
  sections: [
    {
      id: 'accueil',
      title: 'Accueil',
      icon: 'dashboard',
      summary:
        'Tableau de bord formateur : prochaines sessions, rappels de clôture, indicateurs de cohortes et liens rapides vers vos modules principaux.',
      steps: [
        'Après connexion, lisez les bandeaux ou cartes d’alerte (profil incomplet, session à clôturer, travaux non corrigés).',
        'La section sessions à venir indique date, heure, cohorte et salle : cliquez pour ouvrir directement la feuille de séance.',
        'Les indicateurs (nombre d’apprenants, moyennes sommaires) vous aident à prioriser votre semaine.',
        'Utilisez les raccourcis plutôt que le menu complet si vous avez une action urgente (ex. enregistrer les présences du jour).',
        'Actualisez via l’icône en haut à droite si une session vient d’être ajoutée par l’administration et n’apparaît pas encore.',
      ],
      tips: [
        'Consultez l’accueil chaque matin de vacation pour ne pas oublier une clôture de session.',
      ],
      linkTo: '/formateur',
      linkLabel: 'Tableau de bord',
    },
    {
      id: 'sessions',
      title: 'Mes sessions',
      icon: 'event',
      summary:
        'Liste et détail de vos vacations : apprenants inscrits, feuille de présences, saisie des notes/évaluations et clôture officielle de séance.',
      steps: [
        'Ouvrez Mes sessions : les séances sont triées par date (à venir puis passées).',
        'Cliquez sur une session pour voir le détail : cohorte, filière, créneau horaire, salle ou modalité (présentiel / distanciel).',
        'Feuille de présences : cochez Présent, Absent ou Retard pour chaque apprenant au moment de la séance ; enregistrez avant de quitter la page.',
        'Notes / évaluations : saisissez la note sur le barème indiqué (sur 10, sur 20…) ; un commentaire optionnel peut accompagner la note.',
        'Vérifiez qu’aucun apprenant n’est resté sans statut de présence ni sans note si une évaluation était prévue ce jour-là.',
        'Clôturez la session une fois la vacation terminée : la clôture fige les présences et notes pour la période concernée (selon règles du centre).',
        'Une session passée non clôturée peut bloquer la rémunération ou les statistiques : traitez les retards sous 48 h.',
      ],
      tips: [
        'En cas d’apprenant absent non justifié, notez-le dans le commentaire pour le suivi administratif.',
        'Si la liste d’apprenants est vide alors que la salle est pleine, signalez la cohorte et l’heure au secrétariat avant de saisir des données.',
      ],
      linkTo: '/formateur/sessions',
      linkLabel: 'Mes sessions',
    },
    {
      id: 'elearning',
      title: 'E-learning',
      icon: 'play_circle',
      summary:
        'Gestion pédagogique numérique : cours assignés, correction des travaux, ressources liées et modération des forums dont vous êtes responsable.',
      steps: [
        'Ouvrez E-learning : la liste affiche les cours / modules où vous êtes référent ou co-animateur.',
        'Entrez dans un cours pour voir les ressources publiées (vidéos, PDF, exercices) et l’état de complétion des apprenants si disponible.',
        'Travaux à corriger : ouvrez la file « En attente » ; lisez le rendu de l’apprenant, attribuez une note ou un statut (validé, à refaire), ajoutez un commentaire constructif, validez.',
        'Forums : surveillez les nouveaux fils ; répondez aux questions récurrentes, épinglez une FAQ si l’outil le permet, masquez ou signalez les messages inappropriés.',
        'Ressources : vous pouvez parfois ajouter ou lier une ressource à une session — vérifiez les droits accordés par l’administration.',
        'Après correction, l’apprenant reçoit en général une notification : pas besoin de lui écrire en parallèle sauf urgence.',
      ],
      tips: [
        'Corrigez les travaux sous 5 jours ouvrés pour maintenir l’engagement des apprenants.',
        'Utilisez un vocabulaire professionnel dans les forums : vous représentez le centre.',
      ],
      linkTo: '/formateur/elearning',
      linkLabel: 'E-learning formateur',
    },
    {
      id: 'profil',
      title: 'Mon profil',
      icon: 'person',
      summary:
        'Identité professionnelle, spécialité, coordonnées, documents justificatifs et sécurité du compte formateur.',
      steps: [
        'Ouvrez Mon profil dès la première connexion si la plateforme vous y redirige : certains champs sont obligatoires pour débloquer le reste du menu.',
        'Renseignez spécialité, diplômes, années d’expérience selon les champs proposés — ces informations alimentent votre fiche côté administration.',
        'Téléversez les pièces demandées (contrat, CV, pièce d’identité) au format PDF ou JPG lisible.',
        'Mettez à jour email et téléphone pour recevoir les convocations et notifications de planning.',
        'Changez votre mot de passe via la section sécurité ; notez-le dans un gestionnaire de mots de passe sécurisé.',
        'Enregistrez après chaque modification et attendez le message de succès avant de quitter la page.',
      ],
      tips: [
        'Tant que le profil est incomplet, vous serez renvoyé vers cette page à chaque navigation — finalisez tout en une fois.',
        'Une photo professionnelle améliore votre reconnaissance auprès des apprenants sur les listes de présence.',
      ],
      linkTo: '/formateur/profil',
      linkLabel: 'Mon profil',
    },
    {
      id: 'remuneration',
      title: 'Rémunération',
      icon: 'payments',
      summary:
        'Suivi de vos prestations : vacations réalisées, heures comptabilisées, montants dus, statuts de paiement et historique mensuel.',
      steps: [
        'Ouvrez Rémunération : sélectionnez la période (mois en cours ou mois passés).',
        'Chaque ligne correspond à une prestation (session clôturée, heures e-learning, vacation spécifique) avec le montant calculé selon la grille du centre.',
        'Statuts courants : « Due » (à payer), « Payée » (virement effectué), « Annulée » (session invalidée ou corrigée).',
        'Cliquez sur une ligne pour le détail : date, cohorte, durée, taux horaire ou forfait appliqué.',
        'Comparez le total affiché avec vos sessions clôturées dans Mes sessions : un écart signale une session non clôturée ou un barème à clarifier avec la comptabilité.',
        'Les paiements effectifs se font hors plateforme (virement, Mobile Money) selon le processus du centre ; la plateforme affiche le statut une fois enregistré par l’administration.',
      ],
      tips: [
        'Clôturez vos sessions le jour même pour accélérer la comptabilisation.',
        'Conservez vos propres relevés de vacations en parallèle en cas de litige ponctuel.',
      ],
      linkTo: '/formateur/remuneration',
      linkLabel: 'Mes rémunérations',
    },
    {
      id: 'parrainage',
      title: 'Parrainage',
      icon: 'groups',
      summary:
        'Code parrain personnel, partage avec votre réseau et suivi des inscriptions générées — avec commissions éventuelles créditées au portefeuille.',
      steps: [
        'Ouvrez Parrainage : copiez votre code ou partagez le lien / QR affiché.',
        'Expliquez à vos filleuls de saisir le code lors de leur inscription sur la plateforme apprenant.',
        'Suivez la liste : inscription démarrée, payée, dossier validé — chaque étape peut conditionner la commission.',
        'Les montants validés apparaissent dans Portefeuille ; les retraits suivent les mêmes règles que pour les apprenants parrains.',
      ],
      tips: [
        'Ne confondez pas parrainage formateur et parrainage institutionnel partenaire : les barèmes peuvent différer.',
      ],
      linkTo: '/formateur/parrainage',
      linkLabel: 'Parrainage',
    },
    {
      id: 'wallet',
      title: 'Portefeuille',
      icon: 'account_balance_wallet',
      summary:
        'Solde des commissions parrainage, historique des mouvements et demandes de retrait.',
      steps: [
        'Consultez le solde disponible et l’historique (crédits commission, retraits, ajustements).',
        'Lancez une demande de retrait en respectant le montant minimum affiché.',
        'Suivez le statut : en cours, payé, refusé (motif dans l’historique ou notification).',
        'Mettez à jour vos coordonnées de paiement dans Mon profil avant toute demande.',
      ],
      linkTo: '/formateur/wallet',
      linkLabel: 'Portefeuille',
    },
    {
      id: 'performance',
      title: 'Impact académique',
      icon: 'school',
      summary:
        'Indicateurs pédagogiques de vos cohortes : moyennes sur 20, taux de présence, nombre d’apprenants et synthèse globale.',
      steps: [
        'Ouvrez Impact académique : la moyenne globale résume l’ensemble des cohortes que vous animez.',
        'Par cohorte, lisez la moyenne sur 20, le taux de présence moyen et le nombre de sessions déjà animées.',
        'Identifiez les cohortes sous le seuil fixé par le centre pour planifier un soutien renforcé (rattrapage, tutorat).',
        'Ces données proviennent des présences et notes saisies dans Mes sessions : des saisies incomplètes faussent les indicateurs.',
        'Actualisez après clôture de session si les chiffres semblent anciens.',
      ],
      tips: [
        'Un taux de présence faible peut expliquer une moyenne basse : croisez les deux indicateurs avant de conclure.',
      ],
      linkTo: '/formateur/performance',
      linkLabel: 'Impact académique',
    },
  ],
  faqs: [
    {
      question: 'Je ne peux pas accéder aux autres menus.',
      answer:
        'La plateforme bloque la navigation tant que Mon profil est incomplet ou que le mot de passe temporaire n’a pas été changé. Finalisez ces étapes, enregistrez, déconnectez-vous puis reconnectez-vous. Le menu complet se débloque automatiquement.',
    },
    {
      question: 'Une session n’apparaît pas dans Mes sessions.',
      answer:
        'Seules les sessions où vous êtes assigné apparaissent. Vérifiez la date (filtre passé/futur). Si la vacation a été planifiée récemment, actualisez la page. Sinon, contactez le planning administratif avec la filière, la date et l’heure prévues.',
    },
    {
      question: 'Je ne peux plus modifier les notes après clôture.',
      answer:
        'La clôture fige les données pour la rémunération et les statistiques. Pour une correction exceptionnelle, demandez au responsable académique ou à l’administration une réouverture — ne créez pas de doublons de session.',
    },
    {
      question: 'Un apprenant dit avoir rendu un travail mais je ne le vois pas.',
      answer:
        'Vérifiez la file « En attente » et les filtres du cours e-learning. Demandez à l’apprenant une capture de l’écran de confirmation d’envoi. Vérifiez qu’il a rendu dans le bon module et la bonne cohorte.',
    },
  ],
};
